<template>
  <ModalControl
    @closed="$emit('closed')"
    :show="show"
    v-bind:title="'EVENT WAIVER AND RELEASE OF LIABILITY'"
    dismiss
  >
    <div class="text-sm p-2 overflow-y-auto max-h-[70vh]">
      <!-- === EVENT DETAILS === -->
      <div class="pb-2 font-bold border-b mb-2">Event Details</div>
      <div class="mb-4">
        <p>Hosted by: PAFCPIC</p>
        <p>Organized by: JN Enterpraises Branding Consultancy</p>
        <p>In Support of: HERO Foundation</p>
        <p>Event Date: November 15, 2025</p>
        <p>Event Venue: Philippine Army Grandstand, Taguig City</p>
        <p>
          Event Type: Free Outdoor Event (3KM | 5KM | 10KM Run + Zumba + Booths
          + Games)
        </p>
      </div>

      <!-- === AGREEMENT TEXT === -->
      <div class="pb-2 font-bold border-b mb-2">PLEASE READ CAREFULLY</div>
      <div class="mb-4 text-justify">
        By completing and submitting this form, you agree to the terms and
        conditions outlined in this waiver. Your submission will serve as your
        legally binding electronic signature.
      </div>

      <!-- === WAIVER SECTIONS (DATA PRIVACY, ASSUMPTION OF RISK, ETC.) === -->
      <div class="pb-2 font-bold border-b mb-2">1. DATA PRIVACY</div>
      <div class="mb-4 text-justify">
        By participating in this Fun Run event, you consent to PAFCPIC's data
        privacy policy in compliance with the Data Privacy Act of 2012.
        <a
          href="https://pafcpic.ph/privacy-policy"
          class="text-primary font-semibold underline"
          target="_blank"
        >
          Read the Privacy Policy
        </a>
      </div>

      <!-- === ELECTRONIC ACKNOWLEDGMENT === -->
      <div class="pb-2 font-bold border-b mb-2">ELECTRONIC ACKNOWLEDGMENT</div>
      <div class="pl-1 mb-4">
        <!-- Checkboxes -->
        <div class="text-left">
          <!-- 1 -->
          <label class="flex items-start mb-2">
            <input type="checkbox" class="mr-2 mt-[3px]" v-model="checks[0]" />
            <span>
              I have read and understood this waiver and agree to its terms.
              <span class="text-red">*</span>
            </span>
          </label>

          <!-- 2 -->
          <label class="flex items-start mb-2">
            <input type="checkbox" class="mr-2 mt-[3px]" v-model="checks[1]" />
            <span>
              I am voluntarily participating at my own risk.
              <span class="text-red">*</span>
            </span>
          </label>

          <!-- 3 -->
          <label class="flex items-start mb-2">
            <input type="checkbox" class="mr-2 mt-[3px]" v-model="checks[2]" />
            <span>
              This electronic acknowledgment is legally binding.
              <span class="text-red">*</span>
            </span>
          </label>

          <!-- 4 (Only show if minor) -->
          <label
            v-if="is_minor"
            class="flex items-start mb-2"
          >
            <input type="checkbox" class="mr-2 mt-[3px]" v-model="checks[3]" />
            <span>
              (If applicable) I am the parent/legal guardian of a minor
              participant and accept these terms on their behalf.
              <span class="text-red">*</span>
            </span>
          </label>
        </div>

        <!-- === GUARDIAN + SIGNATURE FIELDS === -->
        <div class="mt-4 text-left">
          <!-- ✅ Guardian dropdown (only for minors) -->
          <div v-if="is_minor" class="mb-4">
            <p>
              Select Guardian:
              <span class="text-red">*</span>
              <select
                class="border p-1 rounded-lg w-full"
                v-model="selected_guardian"
                required
              >
                <option value="" disabled>Select Guardian</option>
                <option
                  v-for="(reg, index) in validGuardians"
                  :key="index"
                  :value="reg.fname + ' ' + reg.lname"
                >
                  {{ reg.fname.toUpperCase() }} {{ reg.lname.toUpperCase() }}
                </option>
              </select>
            </p>
          </div>

          <!-- ✅ Guardian Relationship -->
          <div v-if="is_minor" class="mb-4">
            <p>
              Relationship to Guardian:
              <span class="text-red">*</span>
              <input
                type="text"
                class="border p-1 rounded-lg w-full uppercase"
                v-model="relationship"
                required
              />
            </p>
          </div>

          <!-- ✅ Digital Signature (required for everyone) -->
          <p class="mb-2">
            Digital Signature (Type Full Name):
            <span class="text-red">*</span>
            <input
              type="text"
              class="border p-1 rounded-lg w-full uppercase"
              v-model="minor.signature"
              required
            />
          </p>

          <p class="mb-2">
            Date Signed:
            <input
              type="text"
              class="border p-1 rounded-lg w-full bg-gray-100"
              :value="today"
              disabled
            />
          </p>
        </div>
      </div>

      <!-- === ACTION BUTTON === -->
      <div class="flex flex-col mt-4">
        <ButtonControl
          class="mt-2"
          :disabled="!canAgree"
          :class="{ 'opacity-50 cursor-not-allowed': !canAgree }"
          @click="agree"
        >
          {{ this.lang("lbl_0000190") }}
        </ButtonControl>
      </div>
    </div>
  </ModalControl>
</template>

<script>
import ModalControl from "./ModalControl.vue";
import ButtonControl from "./ButtonControl.vue";
import mixin from "./../../mixin.js";

export default {
  name: "v-funrun-waiver-control",
  mixins: [mixin],
  components: { ModalControl, ButtonControl },
  props: {
    show: { type: Boolean, required: true },
    waiver: {
      type: Object,
      default: () => ({
        user_name: "",
        guardian_name: "",
        signature: "",
        is_minor: false,
        relationship: "",
        checks: [false, false, false, false],
      }),
    },
    is_minor: { type: Boolean, default: false },
  },
  data() {
    return {
      checks: [false, false, false, false],
      minor: { signature: "" },
      today: new Date().toLocaleDateString("en-PH"),
      selected_guardian: "",
      relationship: "",
    };
  },
  watch: {
    waiver: {
      immediate: true,
      handler(val) {
        if (!val) return;
        // ensure 4 checks always exist
        const base = val.checks || [];
        this.checks = [
          base[0] || false,
          base[1] || false,
          base[2] || false,
          base[3] || false,
        ];
        this.minor.signature =
          val.signature || val.user_name || val.guardian_name || "";
        this.selected_guardian = val.guardian_name || "";
        this.relationship = val.relationship || "";
      },
    },
  },
  computed: {
    guardians() {
      return this.$store.state.fun_run.registrants || [];
    },
    validGuardians() {
      const selfName = (this.waiver.user_name || "").trim().toLowerCase();
      return this.guardians.filter((reg) => {
        const fullName = `${(reg.fname || "").trim()} ${(reg.lname || "").trim()}`.toLowerCase();
        return reg.age >= 18 && fullName && fullName !== selfName;
      });
    },
    canAgree() {
      const requiredChecks = this.is_minor ? 4 : 3;
      const allRequiredChecked = this.checks.slice(0, requiredChecks).every((v) => v);
      if (!allRequiredChecked) return false;

      if (!this.minor.signature.trim()) return false;
      if (this.is_minor && !this.selected_guardian.trim()) return false;
      if (this.is_minor && !this.relationship.trim()) return false;

      return true;
    },
  },
  methods: {
    agree() {
      if (!this.canAgree) return;

      const registrants = this.$store.state.fun_run.registrants || [];
      const idx = registrants.findIndex(
        (r) =>
          `${r.fname} ${r.lname}`.trim().toLowerCase() ===
          (this.waiver.user_name || "").trim().toLowerCase()
      );

      if (idx !== -1) {
        registrants[idx].signature = this.minor.signature;

        if (this.is_minor) {
          registrants[idx].guardian_name = this.selected_guardian;
          registrants[idx].relationship = this.relationship;
        }
      }

      this.$emit("agreed", {
        guardian_name: this.is_minor ? this.selected_guardian : "",
        relationship: this.is_minor ? this.relationship : "",
        signature: this.minor.signature,
        user_name: this.minor.signature,
        is_minor: this.is_minor,
        checks: this.checks.slice(),
      });
    },
  },
};
</script>

<style scoped>
input[type="checkbox"] {
  transform: scale(1.1);
}
.text-red {
  color: #dc2626;
}
.uppercase {
  text-transform: uppercase;
}
</style>
